import { useDeviceType } from '@/hooks/useDeviceType'
import { DEVICE_TYPE } from '@/hooks/useDeviceType'
import MobileDialogOptions from './components/MobileDialogContent'
import DesktopDialogOptions from './components/DesktopDialogContent'
import { useSourceDialog } from './hooks/useSourceDialog'
import { DateRangeSelector } from './components/DateRangeSelector'
import { Modal } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'

type SourceDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  conversationId: string
}

function SourceDialog(props: SourceDialogProps) {
  const { open, onOpenChange, conversationId } = props

  // Get current device type
  const deviceType = useDeviceType()

  // Use custom hook for dialog logic
  const {
    isResearchEnabled,
    activeTab,
    dateRange,
    showWarningBanner,
    stateSelectionProps,
    handleTabChange,
    handleClearButtonClick,
    handleApplyButtonClick,
    handleDateRangeChange,
  } = useSourceDialog({
    conversationId,
    onOpenChange,
    open,
  })

  // ModalContainer's base classes cap height at 85dvh on mobile and drop the cap (sm:max-h-none) on
  // desktop; re-assert our own width/height on both breakpoints so the dialog keeps its pre-migration
  // dimensions (mobile fills 90vw/95vh; desktop is 90vh but never taller than 794px).
  const containerClassName =
    deviceType !== DEVICE_TYPE.DESKTOP ? 'w-[90vw] max-w-none h-[95vh] max-h-[95vh]' : 'w-[90%] max-w-[860px] h-[90vh] max-h-[794px] sm:max-h-[794px]'

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContainer className={containerClassName}>
        {/* Body */}
        <div className="flex-1 overflow-y-auto md:pb-0 smooth-scroll overscroll-contain">
          {deviceType === DEVICE_TYPE.MOBILE ? (
            // Mobile view
            <MobileDialogOptions
              conversationId={conversationId}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              stateSelectionProps={stateSelectionProps}
              showWarningBanner={showWarningBanner}
              onOpenChange={onOpenChange}
            />
          ) : (
            // Desktop view
            <DesktopDialogOptions
              conversationId={conversationId}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              stateSelectionProps={stateSelectionProps}
              showWarningBanner={showWarningBanner}
              onOpenChange={onOpenChange}
            />
          )}
        </div>

        {/* Footer with date range selector and action buttons */}
        <div className="shrink-0 flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-2 pt-3 pb-4 pr-4 pl-6 border-t border-brand-neutral-200 bg-brand-neutral-50 rounded-b-2xl py-3">
          {/* Date Range Selector */}
          <DateRangeSelector dateLow={dateRange.dateLow} dateHigh={dateRange.dateHigh} onChange={handleDateRangeChange} />

          {/* Action Buttons */}
          <div className="flex gap-3 w-full md:w-auto">
            <button
              className="flex flex-1 md:flex-initial flex-nowrap items-center justify-center gap-1 bg-white text-sm md:w-[90px] h-8 md:h-[29px] text-brand-500 border border-brand-500 px-3 rounded-md hover:bg-brand-neutral-100 disabled:border-true-gray-300 disabled:text-true-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white whitespace-nowrap outline outline-1 outline-brand-500 outline-offset-[-1px] disabled:outline-true-gray-300"
              onClick={() => handleClearButtonClick()}
              type="button"
              disabled={false}
            >
              <span>Clear All</span>
            </button>
            <button
              className="flex flex-1 md:flex-initial flex-nowrap items-center justify-center gap-1 bg-brand-500 md:w-[90px] h-8 md:h-[29px] text-sm text-white px-3 rounded-md hover:bg-brand-800 hover:outline-brand-800 disabled:bg-true-gray-300 disabled:cursor-not-allowed disabled:hover:bg-true-gray-300 whitespace-nowrap outline outline-1 outline-offset-[-1px] outline-brand-500 disabled:outline-true-gray-300"
              onClick={() => handleApplyButtonClick()}
              type="button"
              disabled={!isResearchEnabled}
            >
              <span>Apply</span>
            </button>
          </div>
        </div>
      </ModalContainer>
    </Modal>
  )
}

export default SourceDialog
